import React, { FC, useId, useRef } from 'react';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import SVGInline from 'components/SVGInline/SVGInline';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { useBackdropClose } from 'hooks/useBackdropClose/useBackdropClose';
import icons from 'style';
import translate from 'utils/fnTranslate';
import { PictureWithFallback } from 'components/Picture/PictureWithFallback';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import fallbackImageLandscape from 'assets/images/landscape_fallback.png';
import { withNoScroll } from 'hooks/withNoScroll/withNoScroll';
import { getOfferTypeByTag } from 'utils/fnEntitlement';
import { ModalCloseButton, ModalWrapper, StyledSVG } from 'style/styled-components/reusable.css';
import { SubscribeOption } from 'types/Entitlement';
import { getCurrencySymbol } from 'utils/fnData';
import {
    ButtonContainer,
    ImageWrapper,
    ImageWrapperInner,
    MetaContainer,
    MetaDescription,
    MetaService,
    MetaTitle,
    ModalNavigation,
    OptionsContent,
    StyledButton,
    BackButton,
} from './SubscriptionConfirmation.css';

type PropTypes = {
    onClose: () => void;
    onConfirm: () => void;
    onBack: () => void;
    canGoBack?: boolean;
    selectedOffer: SubscribeOption | null;
    loading: boolean;
};

const SubscriptionConfirm: FC<PropTypes> = ({ onConfirm, onBack, onClose, canGoBack = true, selectedOffer, loading }) => {
    const titleId = useId();
    const descriptionId = useId();
    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const backdropClose = useBackdropClose(onClose);

    useKeyboardShortcuts({
        backspace: canGoBack ? onBack : undefined,
    });

    if (!selectedOffer) {
        return null;
    }

    const buttonText = translate('SUBSCRIBE_FOR_PRICE_BUTTON', { price: `${selectedOffer.price} ${getCurrencySymbol(null)}` });

    return (
        <ModalDialog
            as={ModalWrapper}
            onClose={onClose}
            initialFocusRef={buttonContainerRef}
            opacity={1}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            {...backdropClose}
        >
            <ModalNavigation {...backdropClose}>
                {canGoBack && (
                    <BackButton type="button" onClick={onBack} tabIndex={0} aria-label={translate('BACK_BUTTON')}>
                        <SVGInline svg={icons.icoGalleryLeft} />
                    </BackButton>
                )}
                <ModalCloseButton
                    type="button"
                    data-test-id={'header-rentals-modal-close-button'}
                    onClick={onClose}
                    tabIndex={0}
                    aria-label={translate('CLOSE_BUTTON')}
                >
                    <SVGInline svg={icons.closeIcon} />
                </ModalCloseButton>
            </ModalNavigation>

            <OptionsContent>
                <ImageWrapper>
                    <ImageWrapperInner>
                        <PictureWithFallback
                            src={selectedOffer.poster}
                            fit={'cover'}
                            vPosition={'top'}
                            fallbackImage={fallbackImageLandscape}
                        />
                    </ImageWrapperInner>
                </ImageWrapper>
                <MetaContainer>
                    <MetaTitle id={titleId}>{`${translate('SCREEN_SUBSCRIBE_CONFIRMATION_HEADER', {
                        service_name: selectedOffer.name,
                    })}`}</MetaTitle>
                    <MetaService>{getOfferTypeByTag(selectedOffer?.tags?.[0])}</MetaService>
                    <MetaDescription id={descriptionId}>{selectedOffer.description}</MetaDescription>
                    <ButtonContainer ref={buttonContainerRef}>
                        <StyledButton
                            key={''}
                            label={buttonText}
                            onClick={onConfirm}
                            isPrimary={true}
                            tabIndexOverride={0}
                            data-event-name={'click'}
                            data-track-id={'button_subscribe'}
                            data-button-title={buttonText}
                            data-page-name={'SubscriptionConfirmation'}
                        >
                            <StyledSVG svg={icons.cartIcon} />
                        </StyledButton>
                    </ButtonContainer>
                </MetaContainer>
            </OptionsContent>

            <LoadingIndicator overlay={true} isLoading={loading} />
        </ModalDialog>
    );
};

export const SubscriptionConfirmation = withNoScroll(SubscriptionConfirm);
